import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Eye, EyeOff, Mail, Lock, User, Flame } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { IS_PRELAUNCH, PRICING } from "@/lib/pricing";

export default function SignupPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!agreed) {
      setError("Please agree to the Terms of Service and Privacy Policy.");
      return;
    }
    setLoading(true);
    const result = await register(email.trim().toLowerCase(), password, name.trim());
    setLoading(false);
    if (result.ok) {
      toast.success("Welcome to SayItBetter!");
      navigate("/app", { replace: true });
    } else {
      setError(result.error || "Sign up failed.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F9FB] px-6 py-10" data-testid="signup-page">
      <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-[#1F3A5F] mb-2">Create your account</h1>
          <p className="text-[#6B7280]">Start sending better messages today.</p>
        </div>

        {/* Pre-launch bonus callout */}
        {IS_PRELAUNCH && (
          <div
            className="rounded-2xl p-4 mb-5 border-2 border-[#FEB95A]/40"
            style={{ background: "linear-gradient(135deg, #FFF8EB 0%, #FFE7B8 100%)" }}
            data-testid="signup-prelaunch-bonus"
          >
            <div className="flex items-center gap-2 mb-1">
              <Flame size={14} className="text-[#8B4A00]" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#8B4A00]">
                Early adopter bonus
              </span>
            </div>
            <p className="text-[13px] text-[#1F3A5F] leading-snug">
              Sign up today and you can lock in <strong>1 year of PRO for {PRICING.yearly.price}</strong> —
              no charge today. We'll email you a promo code when we launch on Google Play or the App Store.
            </p>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#2C2C2C]">Name <span className="text-[#A0A7B4] font-normal">(optional)</span></label>
            <div className="relative">
              <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A7B4]" />
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                className="pl-10 h-12 rounded-xl bg-white border-[#E5E7EB] focus:border-[#4FB3A5]"
                data-testid="signup-name-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#2C2C2C]">Email</label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A7B4]" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className="pl-10 h-12 rounded-xl bg-white border-[#E5E7EB] focus:border-[#4FB3A5]"
                data-testid="signup-email-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#2C2C2C]">Password</label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A7B4]" />
              <Input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                className="pl-10 pr-10 h-12 rounded-xl bg-white border-[#E5E7EB] focus:border-[#4FB3A5]"
                data-testid="signup-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A7B4] hover:text-[#1F3A5F]"
                data-testid="signup-toggle-password"
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm text-[#6B7280] cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-[#E5E7EB] text-[#2F5BFF]"
              data-testid="signup-terms-checkbox"
            />
            <span>
              I agree to the{" "}
              <Link to="/terms" className="text-[#2F5BFF] hover:underline font-medium">Terms of Service</Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-[#2F5BFF] hover:underline font-medium">Privacy Policy</Link>.
            </span>
          </label>

          {error && (
            <div
              className="text-sm text-[#E07A7A] bg-[#FEF3F2] border border-[#FECACA] p-3 rounded-xl"
              data-testid="signup-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white font-semibold"
            data-testid="signup-submit-btn"
          >
            {loading ? <Loader2 className="animate-spin" /> : "Create account"}
          </Button>
        </form>

        {/* Guest mode escape hatch — match Onboarding/Login */}
        <div className="flex items-center gap-3 my-5" aria-hidden="true">
          <span className="flex-1 h-px bg-[#E5E7EB]" />
          <span className="text-[11px] uppercase tracking-widest text-[#A0A7B4] font-semibold">
            or
          </span>
          <span className="flex-1 h-px bg-[#E5E7EB]" />
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={() => {
            localStorage.setItem("sib_onboarded", "1");
            navigate("/app", { replace: true });
          }}
          data-testid="signup-guest-btn"
          className="w-full h-12 rounded-xl border-[#E5E7EB] text-[#1F3A5F] font-semibold hover:bg-[#EAF6F4] hover:border-[#4FB3A5]"
        >
          Try without an account →
        </Button>
        <p className="text-center text-[11px] text-[#A0A7B4] mt-2 leading-snug">
          3 free message checks/day · No email needed · Create an account later to save your history
        </p>

        <div className="text-center mt-8 text-sm text-[#6B7280]">
          Already have an account?{" "}
          <Link to="/login" data-testid="signup-login-link" className="text-[#2F5BFF] font-medium hover:underline">
            Log in
          </Link>
        </div>
      </div>
    </div>
  );
}
